import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router'
import { Badge, ORDER_TONE, PAYMENT_TONE } from '../components/badge'
import { DebouncedInput, FilterField, Pager, fieldClass, useListParams } from '../components/list'
import { Button, Card, ErrorNote } from '../components/ui'
import { ApiError } from '../lib/api'
import { customerName } from '../lib/customers-api'
import { formatJalaliDateTime, formatMobile, formatToman, toPersianDigits } from '../lib/fa'
import {
  listPayments,
  PAYMENT_STATUS_LABEL,
  type PaymentListItemDto,
  type PaymentStatus,
} from '../lib/sales-api'

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.messageFa : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/**
 * Every payment attempt, newest first. Filters live in the URL (`?status=&from=&to=&minAmount=
 * &maxAmount=&page=`); amounts there are Toman, and become Rial on the way to the API.
 */
export function PaymentsPage() {
  const { params, page, get, setFilter, goTo, clear } = useListParams()
  const location = useLocation()
  const from = `${location.pathname}${location.search}`

  const payments = useQuery({
    queryKey: ['payments', params.toString()],
    queryFn: () => listPayments(params),
    placeholderData: keepPreviousData,
  })

  return (
    <div className="max-w-7xl">
      <h1 className="mb-1 text-2xl font-bold">پرداخت‌ها</h1>
      <p className="mb-6 text-sm text-muted">
        هر تلاش پرداخت یک ردیف است؛ سفارشی که دو بار پرداختش امتحان شده دو ردیف دارد. این بخش فقط
        برای دیدن است.
      </p>

      <Card className="flex flex-wrap items-end gap-3">
        <FilterField label="وضعیت">
          <select
            className={`${fieldClass} w-40`}
            value={get('status')}
            onChange={(e) => setFilter('status', e.target.value)}
          >
            <option value="">همه</option>
            {(Object.keys(PAYMENT_STATUS_LABEL) as PaymentStatus[]).map((status) => (
              <option key={status} value={status}>
                {PAYMENT_STATUS_LABEL[status]}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="از">
          <input
            type="date"
            className={fieldClass}
            value={get('from')}
            onChange={(e) => setFilter('from', e.target.value)}
          />
        </FilterField>
        <FilterField label="تا">
          <input
            type="date"
            className={fieldClass}
            value={get('to')}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </FilterField>
        <FilterField label="حداقل مبلغ (تومان)">
          <DebouncedInput
            digits
            className="w-40"
            value={get('minAmount')}
            onCommit={(v) => setFilter('minAmount', v)}
          />
        </FilterField>
        <FilterField label="حداکثر مبلغ (تومان)">
          <DebouncedInput
            digits
            className="w-40"
            value={get('maxAmount')}
            onCommit={(v) => setFilter('maxAmount', v)}
          />
        </FilterField>
        <Button variant="secondary" onClick={clear}>
          پاک‌کردن فیلترها
        </Button>
      </Card>

      <Card className="mt-6 overflow-x-auto p-0">
        {payments.isPending ? (
          <p className="p-5 text-sm text-muted">در حال بارگذاری…</p>
        ) : payments.isError ? (
          <div className="p-5">
            <ErrorNote>{messageOf(payments.error)}</ErrorNote>
          </div>
        ) : payments.data.items.length === 0 ? (
          <p className="p-5 text-sm text-muted">پرداختی با این فیلترها پیدا نشد.</p>
        ) : (
          <table className={`w-full text-sm ${payments.isPlaceholderData ? 'opacity-60' : ''}`}>
            <thead className="border-b border-line text-right text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">زمان</th>
                <th className="px-4 py-3 font-medium">مشتری</th>
                <th className="px-4 py-3 font-medium">مبلغ</th>
                <th className="px-4 py-3 font-medium">وضعیت</th>
                <th className="px-4 py-3 font-medium">شماره پیگیری</th>
                <th className="px-4 py-3 font-medium">کارت</th>
                <th className="px-4 py-3 font-medium">سفارش</th>
              </tr>
            </thead>
            <tbody>
              {payments.data.items.map((payment) => (
                <PaymentRow key={payment.id} payment={payment} from={from} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {payments.data ? (
        <Pager
          page={page}
          total={payments.data.total}
          pageSize={payments.data.pageSize}
          noun="پرداخت"
          busy={payments.isFetching}
          onGo={goTo}
        />
      ) : null}
    </div>
  )
}

function PaymentRow({ payment, from }: { payment: PaymentListItemDto; from: string }) {
  const name = customerName(payment.customer)
  return (
    <tr className="border-b border-line align-top last:border-0">
      <td className="whitespace-nowrap px-4 py-3 text-muted">
        {formatJalaliDateTime(payment.requestedAt)}
        <div
          className="text-xs"
          dir="ltr"
          style={{ textAlign: 'right' }}
          title="شناسه درگاه (authority)"
        >
          {payment.gateway} · {payment.authority}
        </div>
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/customers/${payment.customer.id}`}
          state={{ from }}
          className="font-medium hover:text-brand-600"
        >
          {name ?? <span className="text-muted">بدون نام</span>}
        </Link>
        <div
          className="whitespace-nowrap text-xs text-muted"
          dir="ltr"
          style={{ textAlign: 'right' }}
        >
          {formatMobile(payment.customer.mobile)}
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">{formatToman(payment.amount)}</td>
      <td className="px-4 py-3">
        <Badge tone={PAYMENT_TONE[payment.status]}>{payment.statusFa}</Badge>
      </td>
      <td className="px-4 py-3" dir="ltr" style={{ textAlign: 'right' }}>
        {payment.refId ? toPersianDigits(payment.refId) : <span className="text-muted">—</span>}
      </td>
      <td className="px-4 py-3" dir="ltr" style={{ textAlign: 'right' }}>
        {payment.cardMask ? (
          toPersianDigits(payment.cardMask)
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/orders/${payment.order.id}`}
          state={{ from }}
          className="hover:text-brand-600"
          title={payment.order.id}
        >
          <Badge tone={ORDER_TONE[payment.order.status]}>{payment.order.statusFa}</Badge>
        </Link>
        <div className="mt-1 font-mono text-xs text-muted" dir="ltr" style={{ textAlign: 'right' }}>
          {payment.order.id}
        </div>
      </td>
    </tr>
  )
}
